import type { CompleteAttachment, ThreadMessage } from "@assistant-ui/react";
import type { ArtifactListItem } from "../types/artifact";
import type { MessageCreate, MessageFileOut } from "../types/cuadra";

/**
 * Convert assistant-ui ThreadMessage to Cuadra API MessageCreate format
 * Note: Attachments are included in the message but may need to be uploaded separately
 * depending on API requirements
 */
/** Plain text from a single ThreadMessage (user or assistant). */
export function extractThreadMessageText(msg: ThreadMessage): string {
  if (typeof msg.content === "string") {
    return msg.content;
  }
  if (Array.isArray(msg.content)) {
    const textContent = msg.content.find((c) => c.type === "text");
    if (
      textContent &&
      typeof textContent === "object" &&
      "text" in textContent
    ) {
      return (textContent.text as string) ?? "";
    }
  }
  return "";
}

/** Latest user message text in the thread (for host tool routing hints). */
export function getLatestUserText(
  messages: readonly ThreadMessage[],
): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!;
    if (msg.role === "user") {
      return extractThreadMessageText(msg);
    }
  }
  return "";
}

export function convertToCuadraMessages(
  messages: readonly ThreadMessage[],
): MessageCreate[] {
  return messages.map((msg) => {
    const content = extractThreadMessageText(msg);

    return {
      role: msg.role as "system" | "user" | "assistant",
      content,
    };
  });
}

/**
 * Determine the assistant-ui attachment type from a MIME content type.
 */
function resolveAttachmentType(contentType: string | null): string {
  if (!contentType) return "file";
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "document";
  return "file";
}

/**
 * Convert an array of API file objects to assistant-ui `CompleteAttachment` format.
 *
 * @param files      - File metadata from the API (`MessageFileOut[]`)
 * @param resolveUrl - Converts the relative API path (e.g. `/v1/files/{id}/content`)
 *                     to a full URL the browser can fetch.
 */
function convertFilesToAttachments(
  files: MessageFileOut[],
  resolveUrl?: (relativePath: string) => string,
): CompleteAttachment[] {
  return files.map((f) => {
    const type = resolveAttachmentType(f.contentType) as
      | "image"
      | "document"
      | "file";
    // Use the explicit url when available, otherwise derive from the file id
    const relativePath = f.url || `/v1/files/${f.id}/content`;
    const fullUrl = resolveUrl ? resolveUrl(relativePath) : relativePath;

    const content: CompleteAttachment["content"] =
      type === "image"
        ? [{ type: "image" as const, image: fullUrl }]
        : [{ type: "text" as const, text: f.filename }];

    return {
      id: f.id,
      type,
      name: f.filename,
      contentType: f.contentType || "application/octet-stream",
      content,
      status: { type: "complete" as const },
    };
  });
}

/**
 * Append `[artifact:identifier:title:type]` markers to any assistant messages
 * that have linked artifacts.  Call this on raw API messages before converting
 * them to ThreadMessage format so that ArtifactReferenceCard is rendered when
 * loading chat history.
 *
 * @param messages  - Raw API message objects (must have `id`, `role`, `content`)
 * @param artifacts - Artifact list from the REST API for this chat
 * @returns         - The same array with `content` mutated for matched messages
 */
export function injectArtifactMarkers<
  T extends { id: string; role: string; content: string | null },
>(messages: T[], artifacts: ArtifactListItem[]): T[] {
  if (!artifacts.length) return messages;

  const byMessageId = new Map<string, ArtifactListItem[]>();
  for (const a of artifacts) {
    if (!a.messageId) continue;
    const list = byMessageId.get(a.messageId) ?? [];
    list.push(a);
    byMessageId.set(a.messageId, list);
  }

  return messages.map((msg) => {
    if (msg.role !== "assistant") return msg;
    const linked = byMessageId.get(msg.id);
    if (!linked?.length) return msg;
    const markers = linked
      .map((a) => `[artifact:${a.identifier}:${a.title}:${a.type}]`)
      .join("\n\n");
    const base = msg.content ?? "";
    return { ...msg, content: base ? `${base}\n\n${markers}` : markers };
  });
}

/** Roles that assistant-ui's ThreadPrimitive recognises. */
const SUPPORTED_ROLES = new Set(["user", "assistant", "system"]);

/**
 * Returns true for message roles that assistant-ui can render.
 * Filters out `tool` result messages that the backend may include in history.
 */
export function isSupportedRole(role: string): boolean {
  return SUPPORTED_ROLES.has(role);
}

/**
 * Convert Cuadra API MessageOut to assistant-ui ThreadMessage format.
 *
 * @param msg        - The API message object.
 * @param resolveUrl - Optional function to resolve relative file URLs
 *                     (e.g. `client.getUrl.bind(client)`).
 * @param chatId     - Optional chat ID to embed in metadata so feedback
 *                     works on messages loaded from history.
 */
export function convertFromCuadraMessage(
  msg: {
    role: string;
    content: string | null;
    id: string;
    createdAt: string;
    files?: MessageFileOut[] | null;
  },
  resolveUrl?: (relativePath: string) => string,
  chatId?: string,
): ThreadMessage {
  // Build attachments from file metadata (user messages only)
  const attachments =
    msg.role === "user" && msg.files && msg.files.length > 0
      ? convertFilesToAttachments(msg.files, resolveUrl)
      : [];

  const custom: Record<string, unknown> = {};
  if (chatId) custom.cuadraChatId = chatId;
  custom.cuadraServerMessageId = msg.id;

  return {
    id: msg.id,
    role: msg.role as "user" | "assistant" | "system",
    content: [
      {
        type: "text" as const,
        text: msg.content ?? "",
      },
    ],
    createdAt: new Date(msg.createdAt),
    metadata: {
      custom,
    },
    ...(msg.role === "assistant" && {
      status: {
        type: "complete" as const,
        reason: "stop" as const,
      },
      metadata: {
        unstable_state: null,
        unstable_annotations: [],
        unstable_data: [],
        steps: [],
        custom,
      },
    }),
    ...(msg.role === "user" && {
      attachments,
    }),
  } as ThreadMessage;
}
